import React, { useState, useEffect, useRef } from 'react';
import { User } from '../../services/authService';
import { cashflowService, CashflowEntry, CreateCashflowData } from '../../services/cashflowService';
import './Cashflow.css';

interface CashflowFormProps {
  user: User | null;
  entry?: CashflowEntry | null;
  onClose: () => void;
  onSave?: () => void; // Optional callback when entry is saved (for refreshing list)
}

// Helper function to format date for input (YYYY-MM-DD)
const formatDateForInput = (dateString: string | undefined): string => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  } catch {
    return '';
  }
};

// Helper function to format date for API (send as date string YYYY-MM-DD)
// The backend will handle this as a date-only value to avoid timezone issues
const formatDateForAPI = (dateString: string): string => {
  if (!dateString) return '';
  // Validate the date string format (YYYY-MM-DD)
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return dateString;
  }
  return '';
};

const CashflowForm: React.FC<CashflowFormProps> = ({ user, entry, onClose, onSave }) => {

  const [formData, setFormData] = useState<CreateCashflowData>({
    amount: 0,
    category: '',
    sub_category: '',
    notes: '',
    created_by: user?.email || 'locvthanh@gmail.com',
    created_at: entry?.created_at || new Date().toISOString()
  });
  
  const [dateInput, setDateInput] = useState<string>(
    formatDateForInput(entry?.created_at || new Date().toISOString())
  );
  const [loading, setLoading] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categories, setCategories] = useState<string[]>([]);
  const [categorySearch, setCategorySearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const categoryModalRef = useRef<HTMLDivElement>(null);
  const [showSubCategoryModal, setShowSubCategoryModal] = useState(false);
  const [subCategories, setSubCategories] = useState<string[]>([]);
  const [subCategorySearch, setSubCategorySearch] = useState('');
  const [selectedSubCategory, setSelectedSubCategory] = useState('');
  const subCategoryModalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Fetch categories when component mounts
    const fetchCategories = async () => {
      try {
        const response = await cashflowService.getCategories();
        if (response.success && response.categories) {
          setCategories(response.categories);
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    if (entry) {
      const formattedDate = formatDateForInput(entry.created_at);
      setFormData({
        amount: entry.amount,
        category: entry.category || '',
        sub_category: entry.sub_category || '',
        notes: entry.notes || '',
        created_by: entry.created_by,
        created_at: entry.created_at
      });
      setDateInput(formattedDate);
      setSelectedCategory(entry.category || '');
      setSelectedSubCategory(entry.sub_category || '');
    }
  }, [entry]);

  // Close category modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Don't close if clicking inside the form modal
      const formModal = document.querySelector('.cashflow-form-modal');
      if (formModal && formModal.contains(event.target as Node)) {
        return;
      }

      if (
        categoryModalRef.current &&
        !categoryModalRef.current.contains(event.target as Node)
      ) {
        setShowCategoryModal(false);
      }
      if (
        subCategoryModalRef.current &&
        !subCategoryModalRef.current.contains(event.target as Node)
      ) {
        setShowSubCategoryModal(false);
      }
    };

    if (showCategoryModal || showSubCategoryModal) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showCategoryModal, showSubCategoryModal]);

  const resetForm = () => {
    const today = new Date().toISOString();
    setFormData({
      amount: 0,
      category: '',
      sub_category: '',
      notes: '',
      created_by: user?.email || 'locvthanh@gmail.com',
      created_at: today
    });
    setDateInput(formatDateForInput(today));
    setSelectedCategory('');
    setSelectedSubCategory('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.amount || formData.amount === 0) {
      alert('Amount is required');
      return;
    }

    try {
      setLoading(true);
      
      // Format date for API if date input is provided
      const submitData = {
        ...formData,
        created_at: dateInput ? formatDateForAPI(dateInput) : undefined
      };
      
      if (entry) {
        await cashflowService.updateCashflowEntry(entry.id, submitData);
      } else {
        await cashflowService.createCashflowEntry(submitData);
      }
      
      if (onSave) {
        onSave();
      }
      
      onClose();
    } catch (error) {
      console.error('Error saving entry:', error);
      alert('Failed to save entry');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAndContinue = async (e: React.MouseEvent) => {
    e.preventDefault();
    
    if (!formData.amount || formData.amount === 0) {
      alert('Amount is required');
      return;
    }

    try {
      setLoading(true);
      
      // Format date for API if date input is provided
      const submitData = {
        ...formData,
        created_at: dateInput ? formatDateForAPI(dateInput) : undefined
      };
      
      await cashflowService.createCashflowEntry(submitData);
      
      // Refresh the list if callback provided
      if (onSave) {
        onSave();
      }
      
      // Reset form for next entry
      resetForm();
    } catch (error) {
      console.error('Error saving entry:', error);
      alert('Failed to save entry');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof CreateCashflowData, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCategoryClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowCategoryModal(true);
    setCategorySearch('');
    setSelectedCategory(formData.category || '');
    // Refetch categories when opening modal to get latest data
    const fetchCategories = async () => {
      try {
        const response = await cashflowService.getCategories();
        if (response.success && response.categories) {
          setCategories(response.categories);
        } else {
          console.warn('Categories fetch returned:', response);
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  };

  const handleCategorySelect = (category: string) => {
    setSelectedCategory(category);
    setCategorySearch(category); // Update search when selecting
  };

  const handleCategorySearchChange = (value: string) => {
    setCategorySearch(value);
    // If search matches an existing category exactly, select it
    const exactMatch = categories.find(cat => cat.toLowerCase() === value.toLowerCase());
    if (exactMatch) {
      setSelectedCategory(exactMatch);
    } else {
      setSelectedCategory('');
    }
  };

  const handleCategoryDone = () => {
    // If user typed something and it doesn't exist, add it
    if (categorySearch.trim() && !categories.find(cat => cat.toLowerCase() === categorySearch.trim().toLowerCase())) {
      const newCategory = categorySearch.trim();
      setFormData(prev => ({ ...prev, category: newCategory }));
      setCategories([...categories, newCategory].sort());
    } 
    // If user selected a category from the list
    else if (selectedCategory) {
      setFormData(prev => ({ ...prev, category: selectedCategory }));
    }
    // If user typed something that matches an existing category
    else if (categorySearch.trim()) {
      const matchingCategory = categories.find(cat => cat.toLowerCase() === categorySearch.trim().toLowerCase());
      if (matchingCategory) {
        setFormData(prev => ({ ...prev, category: matchingCategory }));
      } else {
        // If no match but user typed something, save as typed
        setFormData(prev => ({ ...prev, category: categorySearch.trim() }));
      }
    }
    setShowCategoryModal(false);
    setCategorySearch('');
    setSelectedCategory('');
  };

  const filteredCategories = categorySearch
    ? categories.filter(cat =>
        cat.toLowerCase().includes(categorySearch.toLowerCase())
      )
    : categories;

  const handleSubCategoryClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowSubCategoryModal(true);
    setSubCategorySearch('');
    setSelectedSubCategory(formData.sub_category || '');
    // Refetch sub-categories when opening modal to get latest data
    const fetchSubCategories = async () => {
      try {
        const response = await cashflowService.getSubCategories(formData.category || undefined);
        if (response.success && response.sub_categories) {
          setSubCategories(response.sub_categories);
        } else {
          console.warn('Sub-categories fetch returned:', response);
        }
      } catch (error) {
        console.error('Error fetching sub-categories:', error);
      }
    };
    fetchSubCategories();
  };

  const handleSubCategorySelect = (subCategory: string) => {
    setSelectedSubCategory(subCategory);
    setSubCategorySearch(subCategory); // Update search when selecting
  };

  const handleSubCategorySearchChange = (value: string) => {
    setSubCategorySearch(value);
    // If search matches an existing sub-category exactly, select it
    const exactMatch = subCategories.find(subCat => subCat.toLowerCase() === value.toLowerCase());
    if (exactMatch) {
      setSelectedSubCategory(exactMatch);
    } else {
      setSelectedSubCategory('');
    }
  };

  const handleSubCategoryDone = () => {
    // If user typed something and it doesn't exist, add it
    if (subCategorySearch.trim() && !subCategories.includes(subCategorySearch.trim())) {
      const newSubCategory = subCategorySearch.trim();
      setFormData(prev => ({ ...prev, sub_category: newSubCategory }));
      setSubCategories([...subCategories, newSubCategory].sort());
    } 
    // If user selected a sub-category from the list
    else if (selectedSubCategory) {
      setFormData(prev => ({ ...prev, sub_category: selectedSubCategory }));
    }
    // If user typed something that matches an existing sub-category
    else if (subCategorySearch.trim() && subCategories.includes(subCategorySearch.trim())) {
      setFormData(prev => ({ ...prev, sub_category: subCategorySearch.trim() }));
    }
    setShowSubCategoryModal(false);
    setSubCategorySearch('');
    setSelectedSubCategory('');
  };

  const filteredSubCategories = subCategorySearch
    ? subCategories.filter(subCat =>
        subCat.toLowerCase().includes(subCategorySearch.toLowerCase())
      )
    : subCategories;

  return (
    <div className="cashflow-form-overlay" onClick={onClose}>
      <div className="cashflow-form-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cashflow-form-header">
          <h2>{entry ? 'Edit Entry' : 'Add Entry'}</h2>
          <button className="cashflow-form-close" onClick={onClose}>×</button>
        </div>
        
        <form className="cashflow-form" onSubmit={handleSubmit}>
          <div className="cashflow-form-group">
            <label>Amount *</label>
            <input
              type="number"
              value={formData.amount || ''}
              onChange={(e) => handleChange('amount', parseFloat(e.target.value) || 0)}
              required
              placeholder="Enter amount"
            />
          </div>

          <div className="cashflow-form-group">
            <label>Date</label>
            <input
              type="date"
              value={dateInput}
              onChange={(e) => {
                setDateInput(e.target.value);
                if (e.target.value) {
                  setFormData(prev => ({
                    ...prev,
                    created_at: formatDateForAPI(e.target.value)
                  }));
                }
              }}
            />
          </div>

          <div className="cashflow-form-group">
            <label>Category</label>
            <div className="cashflow-category-input-wrapper" onClick={handleCategoryClick}>
              <input
                type="text"
                value={formData.category || ''}
                readOnly
                placeholder="Category"
                className="cashflow-category-input"
              />
              <span className="cashflow-category-arrow">▼</span>
            </div>
          </div>

          <div className="cashflow-form-group">
            <label>Sub Category</label>
            <div className="cashflow-category-input-wrapper" onClick={handleSubCategoryClick}>
              <input
                type="text"
                value={formData.sub_category || ''}
                readOnly
                placeholder="Sub category"
                className="cashflow-category-input"
              />
              <span className="cashflow-category-arrow">▼</span>
            </div>
          </div>

          <div className="cashflow-form-group">
            <label>Notes</label>
            <textarea
              value={formData.notes || ''}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Additional notes"
              rows={3}
            />
          </div>

          <div className="cashflow-form-actions">
            <button type="button" onClick={onClose} className="cashflow-form-cancel">
              Cancel
            </button>
            {!entry && (
              <button 
                type="button" 
                onClick={handleCreateAndContinue} 
                className="cashflow-form-continue" 
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Create and continue'}
              </button>
            )}
            <button type="submit" className="cashflow-form-submit" disabled={loading}>
              {loading ? 'Saving...' : entry ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>

      {/* Category Selection Modal */}
      {showCategoryModal && (
        <div 
          className="cashflow-category-modal-overlay"
          onClick={(e) => {
            // Only close category modal if clicking directly on overlay, not on modal content
            if (e.target === e.currentTarget) {
              setShowCategoryModal(false);
            }
          }}
        >
          <div 
            ref={categoryModalRef} 
            className="cashflow-category-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="cashflow-category-modal-title">category</h3>
            <input
              type="text"
              className="cashflow-category-search"
              placeholder="Add or search"
              value={categorySearch}
              onChange={(e) => handleCategorySearchChange(e.target.value)}
              autoFocus
              autoCapitalize="off"
              autoCorrect="off"
            />
            <div className="cashflow-category-list">
              {filteredCategories.length > 0 ? (
                filteredCategories.map((category) => (
                  <label key={category} className="cashflow-category-item">
                    <input
                      type="radio"
                      name="category"
                      value={category}
                      checked={selectedCategory === category}
                      onChange={() => handleCategorySelect(category)}
                    />
                    <span>{category}</span>
                  </label>
                ))
              ) : categorySearch ? (
                <div className="cashflow-category-empty">
                  No categories found. Type and click Done to add "{categorySearch}"
                </div>
              ) : categories.length === 0 ? (
                <div className="cashflow-category-empty">
                  No categories available. Type and click Done to add a new category.
                </div>
              ) : null}
            </div>
            <div className="cashflow-category-modal-actions">
              <button
                type="button"
                className="cashflow-category-done"
                onClick={handleCategoryDone}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sub-Category Selection Modal */}
      {showSubCategoryModal && (
        <div 
          className="cashflow-category-modal-overlay"
          onClick={(e) => {
            // Only close sub-category modal if clicking directly on overlay, not on modal content
            if (e.target === e.currentTarget) {
              setShowSubCategoryModal(false);
            }
          }}
        >
          <div 
            ref={subCategoryModalRef} 
            className="cashflow-category-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="cashflow-category-modal-title">sub category</h3>
            <input
              type="text"
              className="cashflow-category-search"
              placeholder="Add or search"
              value={subCategorySearch}
              onChange={(e) => handleSubCategorySearchChange(e.target.value)}
              autoFocus
            />
            <div className="cashflow-category-list">
              {filteredSubCategories.length > 0 ? (
                filteredSubCategories.map((subCategory) => (
                  <label key={subCategory} className="cashflow-category-item">
                    <input
                      type="radio"
                      name="subCategory"
                      value={subCategory}
                      checked={selectedSubCategory === subCategory}
                      onChange={() => handleSubCategorySelect(subCategory)}
                    />
                    <span>{subCategory}</span>
                  </label>
                ))
              ) : subCategorySearch ? (
                <div className="cashflow-category-empty">
                  No sub-categories found. Type and click Done to add "{subCategorySearch}"
                </div>
              ) : subCategories.length === 0 ? (
                <div className="cashflow-category-empty">
                  No sub-categories available. Type and click Done to add a new sub-category.
                </div>
              ) : null}
            </div>
            <div className="cashflow-category-modal-actions">
              <button
                type="button"
                className="cashflow-category-done"
                onClick={handleSubCategoryDone}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CashflowForm;

